import fp from "lodash/fp";

import { getAutoCloseAt } from "hooks/terminalSolution/terminalSolutionMappers";

const mapFileBuild = item => {
	return {
		datawire_id: item?.datawireId,
		dlid: item?.dlid,
		file_build_option: item?.fileBuild?.fileBuildOption,
		file_type: item?.fileBuild?.fileType,
		tsys_terminal_settings: {},
	};
};

const checkBoolean = item => {
	return item === "true" || item === true;
};

const mapSubscriptionItem = item => {
	return {
		billing_cadence: item?.billingCadence?.name,
		cost: item?.cost,
		is_subscribed: checkBoolean(item?.isSubscribed),
		quantity: item?.quantity,
	};
};

const applicationSummaryTerminalMapper = ({
	softwareOptionsTerminalForm,
	softwareOptionsGatewayForm,
	softwareOptionsPointOfSaleForm,
}: {
	softwareOptionsTerminalForm: { [key: string]: any };
	softwareOptionsGatewayForm: { [key: string]: any };
	softwareOptionsPointOfSaleForm: { [key: string]: any };
}) => {
	// Software Info Terminals
	const terminalItems = softwareOptionsTerminalForm?.map(item => ({
		back_end_platform: item.terminal?.backEndPlatform,
		file_build: mapFileBuild(item.terminal),
		front_end_platforms: [], // check
		id: item.id,
		other_back_end_platform_name: item.terminal?.otherBackEndPlatformName,
		other_front_end_platform_name: item.terminal?.otherFrontEndPlatformName,
		quantity: item.terminal?.quantity,
		status: "draft", // check??
		terminal: {
			auto_close_at: getAutoCloseAt({
				...item.terminal,
				isAutoCloseEnabled: checkBoolean(
					item.terminal?.isAutoCloseEnabled,
				),
			})?.auto_close_at,
			clover_terminal_settings: {
				clover_id: item.terminal?.cloverTerminalSettings?.cloverId,
				go_monthly_fee:
					item.terminal?.cloverTerminalSettings?.goMonthlyFee,
				services_per_device_fee:
					item.terminal?.cloverTerminalSettings?.servicesPerDeviceFee,
				username: item.terminal?.cloverTerminalSettings?.username,
			},
			connection_backup: item.terminal?.connectionBackup,
			connection_primary: item.terminal?.connectionPrimary,
			equipment_insurance: item.terminal?.equipmentInsurance,
			estimated_tax: item.terminal?.estimatedTax,
			industry_type: item.terminal?.industryType,
			is_auto_close_enabled: checkBoolean(
				item.terminal?.isAutoCloseEnabled,
			),
			is_transarmor_enabled: checkBoolean(
				item.terminal?.isTransarmorEnabled,
			),
			is_tsys_tokenization_enabled: checkBoolean(
				item.terminal?.isTsysTokenizationEnabled,
			),
			manufacturer: item.terminal?.manufacturer,
			manufacturer_id: item.terminal?.manufacturerId,
			model: item.terminal?.model,
			model_id: item.terminal?.modelId,
			month_rental_price: item.terminal?.monthRentalPrice,
			other_manufacturer_name: item.terminal?.otherManufacturerName,
			other_model_name: item.terminal?.otherModelName,
			price_per_terminal: item.terminal?.pricePerTerminal,
			shipping_and_handling: item.terminal?.shippingAndHandling,
			state_tax: item.terminal?.stateTax,
			terminal_accessories: item.terminalAccessories?.map(accessory => ({
				id: accessory.id,
				other_name: accessory.otherName,
				price: accessory.price,
				serial_number: accessory.serialNumber,
				type: accessory.type?.name,
				type_id: accessory.type?.id,
			})),
			terminal_configuration_option:
				item.terminal?.terminalConfigurationOption,
			terminal_options: Object.keys(
				item.terminal?.terminalOptions,
			).filter(
				key =>
					item.terminal?.terminalOptions[key] === true
						? fp.snakeCase(key)
						: null, //check
			),
			terminal_type: item.terminal?.terminalType?.value,
			transarmor_monthly_fee: item.terminal?.transarmorMonthlyFee,
			transarmor_per_transaction_fee:
				item.terminal?.transarmorPerTransactionFee,
			zero_cost_program_type: 0,
		},
	}));

	// Software Info Gateway
	const gatewayItem = {
		back_end_platform: softwareOptionsGatewayForm?.gateway?.backEndPlatform,
		file_build: mapFileBuild(softwareOptionsGatewayForm?.gateway),
		front_end_platforms: [], // check
		gateway: {
			gateway_configuration_option:
				softwareOptionsGatewayForm?.gateway?.gatewayConfigurationOption,
			gateway_name:
				softwareOptionsGatewayForm?.gateway?.gatewayName?.name,
			gateway_name_id:
				softwareOptionsGatewayForm?.gateway?.gatewayName?.id,
			is_cash_discount_enabled: checkBoolean(
				softwareOptionsGatewayForm?.gateway?.isCashDiscountEnabled,
			),
			is_customer_vault_or_recurring_billing: checkBoolean(
				softwareOptionsGatewayForm?.gateway
					?.isCustomerVaultOrRecurringBilling,
			),
			is_level_2_and_3_data_enabled: checkBoolean(
				softwareOptionsGatewayForm?.gateway?.isLevel2And3DataEnabled,
			),
			is_transarmor_enabled: checkBoolean(
				softwareOptionsGatewayForm?.gateway?.isTransarmorEnabled,
			),
			is_tsys_tokenization_enabled: checkBoolean(
				softwareOptionsGatewayForm?.gateway?.isTsysTokenizationEnabled,
			),
			level_3_per_transaction_fee:
				softwareOptionsGatewayForm?.gateway?.level3PerTransactionFee,
			monthly_fee: softwareOptionsGatewayForm?.gateway?.monthlyFee,
			other_gateway_name:
				softwareOptionsGatewayForm?.gateway?.otherGatewayName,
			setup_fee: softwareOptionsGatewayForm?.gateway?.setupFee,
			transaction_fee:
				softwareOptionsGatewayForm?.gateway?.transactionFee,
			transarmor_monthly_fee:
				softwareOptionsGatewayForm?.gateway?.transarmorMonthlyFee,
			transarmor_per_transaction_fee:
				softwareOptionsGatewayForm?.gateway
					?.transarmorPerTransactionFee,
			vault_monthly_fee:
				softwareOptionsGatewayForm?.gateway?.vaultMonthlyFee,
		},
		id: softwareOptionsGatewayForm?.id,
		other_back_end_platform_name:
			softwareOptionsGatewayForm?.gateway?.otherBackEndPlatformName,
		other_front_end_platform_name:
			softwareOptionsGatewayForm?.gateway?.otherFrontEndPlatformName,
		quantity: softwareOptionsGatewayForm?.id ? 1 : 0, // check
		status: "draft", // check
		zero_cost_program_type: 0,
	};

	// Software Info Point of Sale
	const pointOfSaleItem = {
		back_end_platform:
			softwareOptionsPointOfSaleForm?.pointOfSale?.backEndPlatform,
		file_build: mapFileBuild(softwareOptionsPointOfSaleForm?.pointOfSale),
		front_end_platforms: [], // check
		id: softwareOptionsPointOfSaleForm?.id,
		other_back_end_platform_name:
			softwareOptionsPointOfSaleForm?.pointOfSale
				?.otherBackEndPlatformName,
		other_front_end_platform_name:
			softwareOptionsPointOfSaleForm?.pointOfSale
				?.otherFrontEndPlatformName,
		point_of_sale: {
			authorization_version:
				softwareOptionsPointOfSaleForm?.pointOfSale
					?.authorizationVersion,
			is_transarmor_enabled: checkBoolean(
				softwareOptionsPointOfSaleForm?.pointOfSale
					?.isTransarmorEnabled,
			),
			is_tsys_tokenization_enabled: checkBoolean(
				softwareOptionsPointOfSaleForm?.pointOfSale
					?.isTsysTokenizationEnabled,
			),
			merchant_type:
				softwareOptionsPointOfSaleForm?.pointOfSale?.merchantType?.name,
			name_if_other:
				softwareOptionsPointOfSaleForm?.pointOfSale?.nameIfOther,
			product: softwareOptionsPointOfSaleForm?.pointOfSale?.product?.name,
			subscriptions: {
				discount:
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.discount,
				ip_phone_service: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.ipPhoneService,
				),
				marketing_services: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.marketingServices,
				),
				pos_system: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.posSystem,
				),
				salon_in_the_box: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.salonInTheBox,
				),
				sms_message: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.smsMessage,
				),
				sms_service: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.smsService,
				),
				sms_setup: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.smsSetup,
				),
				tap_to_pay: mapSubscriptionItem(
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.tapToPay,
				),
				total_price:
					softwareOptionsPointOfSaleForm?.pointOfSale?.subscriptions
						?.totalPrice,
			},
			transarmor_monthly_fee:
				softwareOptionsPointOfSaleForm?.pointOfSale
					?.transarmorMonthlyFee,
			transarmor_per_transaction_fee:
				softwareOptionsPointOfSaleForm?.pointOfSale
					?.transarmorPerTransactionFee,
		},
		quantity: softwareOptionsPointOfSaleForm?.id ? 1 : 0, // check
		status: "draft", // check
		zero_cost_program_type: 0,
	};

	const items = terminalItems ? [...terminalItems] : [];

	if (softwareOptionsGatewayForm?.id) {
		items.push(gatewayItem);
	}

	if (softwareOptionsPointOfSaleForm?.id) {
		items.push(pointOfSaleItem);
	}

	return {
		count:
			terminalItems?.length +
			gatewayItem?.quantity +
			pointOfSaleItem?.quantity,
		items,
	};
};

export { applicationSummaryTerminalMapper };
